import { useMemo, useState, type ReactNode, type Ref } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type PaginationState,
  type SortingFn,
  type SortingState,
} from '@tanstack/react-table';
import { Pagination } from './Pagination';

export interface DataColumn<T> {
  /** Stable column id (also the sort key). */
  id: string;
  /** Header label (translated copy). */
  header: string;
  /** Cell renderer. */
  cell: (row: T) => ReactNode;
  /** Value used for sorting (number or string). Omit ⇒ column not sortable. */
  sortValue?: (row: T) => number | string;
  align?: 'left' | 'right';
  /** Fixed column width in px. */
  width?: number;
}

export interface DataTableProps<T> {
  columns: DataColumn<T>[];
  rows: T[];
  /** Stable row id — also the deterministic sort tiebreak. */
  getRowId: (row: T) => string;
  /** Initial sort; defaults to the row order supplied. */
  initialSort?: { id: string; desc: boolean };
  /** Rendered in place of the table body when there are no rows. */
  emptyState?: ReactNode;
  /** Min table width before horizontal scroll kicks in. */
  minWidth?: number;
  /**
   * Initial rows per page. Set it to page the rows client-side, with the shared
   * `Pagination` footer. Leave it out to render every row.
   */
  pageSize?: number;
  /** Choices for the rows-per-page selector (only used with `pageSize`). */
  pageSizeOptions?: number[];
  /** Label for the rows-per-page selector (translated copy). */
  rowsPerPageLabel?: string;
  ref?: Ref<HTMLDivElement>;
}

const HEAD_SX = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  color: 'text.secondary',
  borderColor: 'divider',
  bgcolor: 'tableHeaderBg',
  py: 1,
  whiteSpace: 'nowrap',
} as const;

const CELL_SX = { fontSize: 13, borderColor: 'borderSubtle', py: 1.25 } as const;

function compare(a: number | string, b: number | string): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b));
}

/**
 * Client-side, column-driven data grid on TanStack Table v8 (core + sorted row
 * models) — the MUI port of the prototype `DataTable`, for the small, already-
 * fetched preview tables (reports, line lists) that aren't server-paginated.
 * Pass `pageSize` to page a large already-fetched set on the client.
 * Sorting is stable and deterministic: every sortable column breaks ties by the
 * row id, so a tied cluster can't reorder between renders. `PagedTable` remains
 * the choice for server-paginated lists.
 */
export function DataTable<T>({
  columns,
  rows,
  getRowId,
  initialSort,
  emptyState,
  minWidth = 640,
  pageSize,
  pageSizeOptions,
  rowsPerPageLabel,
  ref,
}: DataTableProps<T>) {
  const paged = pageSize != null && pageSize > 0;
  const [sorting, setSorting] = useState<SortingState>(
    initialSort ? [{ id: initialSort.id, desc: initialSort.desc }] : [],
  );
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSize ?? 0,
  });
  // A new `rows` array (another invoice, a refetch) starts again at the first
  // page, so the index never points past the end of a shorter set.
  const [pagedRows, setPagedRows] = useState(rows);
  if (pagedRows !== rows) {
    setPagedRows(rows);
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }

  // Break ties by the stable row id so equal sort values keep a deterministic
  // order regardless of input order or render timing. TanStack negates this
  // comparator for a descending sort, so ties order by id ascending under an asc
  // primary and descending under a desc primary — either way deterministic (ids
  // are unique, so no row can duplicate or skip across a page boundary).
  const tiebreak: SortingFn<T> = (rowA, rowB, columnId) => {
    const col = columns.find((c) => c.id === columnId);
    const primary = col?.sortValue
      ? compare(col.sortValue(rowA.original), col.sortValue(rowB.original))
      : 0;
    if (primary !== 0) return primary;
    return getRowId(rowA.original).localeCompare(getRowId(rowB.original));
  };

  const tableColumns = useMemo<ColumnDef<T>[]>(
    () =>
      columns.map((col) => ({
        id: col.id,
        header: col.header,
        cell: ({ row }) => col.cell(row.original),
        enableSorting: !!col.sortValue,
        sortingFn: tiebreak,
        // A no-op accessor keeps getValue defined; sorting reads sortValue via tiebreak.
        accessorFn: (row: T) => (col.sortValue ? col.sortValue(row) : undefined),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns],
  );

  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Table's useReactTable returns non-memoizable functions by design; the React Compiler safely skips this component and the table is re-created each render as intended.
  const table = useReactTable({
    data: rows,
    columns: tableColumns,
    state: paged ? { sorting, pagination } : { sorting },
    // Paging runs after sorting, so a sort orders the whole set, not just the
    // visible page, and sends the table back to the first page. The resets are
    // explicit: TanStack's autoResetPageIndex does not fire for controlled
    // pagination state.
    onSortingChange: (updater) => {
      setSorting(updater);
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    },
    onPaginationChange: setPagination,
    autoResetPageIndex: false,
    getRowId: (row) => getRowId(row),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(paged ? { getPaginationRowModel: getPaginationRowModel() } : {}),
  });

  const colById = useMemo(() => new Map(columns.map((c) => [c.id, c])), [columns]);

  if (rows.length === 0 && emptyState) {
    return (
      <Card ref={ref} variant="outlined">
        {emptyState}
      </Card>
    );
  }

  return (
    <Card ref={ref} variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth }}>
          <TableHead>
            <TableRow>
              {table.getFlatHeaders().map((header) => {
                const col = colById.get(header.column.id);
                const sortable = header.column.getCanSort();
                const dir = header.column.getIsSorted();
                return (
                  <TableCell
                    key={header.id}
                    align={col?.align ?? 'left'}
                    sortDirection={dir === false ? false : dir}
                    sx={{ ...HEAD_SX, width: col?.width }}
                  >
                    {sortable ? (
                      <TableSortLabel
                        active={dir !== false}
                        direction={dir === false ? 'asc' : dir}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {col?.header}
                      </TableSortLabel>
                    ) : (
                      col?.header
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} hover>
                {row.getVisibleCells().map((cell) => {
                  const col = colById.get(cell.column.id);
                  return (
                    <TableCell
                      key={cell.id}
                      align={col?.align ?? 'left'}
                      sx={{
                        ...CELL_SX,
                        ...(col?.align === 'right' ? { fontVariantNumeric: 'tabular-nums' } : {}),
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      {paged && (
        <Pagination
          total={rows.length}
          take={pagination.pageSize}
          skip={pagination.pageIndex * pagination.pageSize}
          onPageChange={(skip) => table.setPageIndex(Math.floor(skip / pagination.pageSize))}
          onPageSizeChange={
            pageSizeOptions ? (take) => setPagination({ pageIndex: 0, pageSize: take }) : undefined
          }
          pageSizeOptions={pageSizeOptions}
          rowsPerPageLabel={rowsPerPageLabel}
        />
      )}
    </Card>
  );
}
